import {
  type Currency,
  currencyFractionDigits,
  DEFAULT_CURRENCY,
  resolveCurrency,
  USD,
} from "@loot-os/api-contract";
import { skipToken, useQuery } from "@tanstack/react-query";
import { useParams } from "@tanstack/react-router";
import { useMemo } from "react";

import { useTRPC } from "./trpc";

/**
 * The environment's display currency, for every dashboard page that shows or takes an
 * amount. Reads the same `storefront.get` query as `useStorefrontConfigQuery`, so it is one
 * request per environment shared by every caller, and it follows the environment switcher
 * (the request carries x-loot-environment; the switcher resets the query).
 *
 * Outside /tenants/$tenantId there is no tenant to ask — the box template editor edits a
 * box that belongs to no tenant — so the query is skipped rather than sent, and amounts read as
 * dollars: box templates are priced in cents for every tenant, and each storefront renders them
 * in its own currency. While the config is loading (or failed) the default currency stands in;
 * both are USD-shaped, so nothing jumps when the row lands.
 */
export function useCurrency(): Currency {
  const trpc = useTRPC();
  const { tenantId } = useParams({ strict: false }) as { tenantId?: string };
  const storefront = useQuery(trpc.storefront.get.queryOptions(tenantId ? undefined : skipToken));
  const config = storefront.data?.config;
  return useMemo(() => {
    if (!tenantId) return USD;
    return config ? resolveCurrency(config) : DEFAULT_CURRENCY;
  }, [tenantId, config]);
}

export type CurrencyPreset = "credits" | "custom";

/**
 * "credits" when the currency is the default one — name, symbol and multiplier equal
 * `DEFAULT_CURRENCY` and no icon is uploaded. The plural is free: "credit"/"creditz" is still
 * credits. Anything else is a custom currency.
 */
export function currencyPreset(currency: Currency): CurrencyPreset {
  const isCredits =
    currency.name === DEFAULT_CURRENCY.name &&
    currency.symbol === DEFAULT_CURRENCY.symbol &&
    currency.multiplier === DEFAULT_CURRENCY.multiplier &&
    currency.iconSvg === "";
  return isCredits ? "credits" : "custom";
}

/**
 * A table header over cents: "Price" when the cells carry a symbol ("$12.50"), "Price (coins)"
 * when they do not, so a bare "1,250" is never left without its unit.
 */
export function amountColumnHeader(label: string, currency: Currency): string {
  return currency.symbol === "" ? `${label} (${currency.namePlural})` : label;
}

/** An input label that always names what to type: "Amount ($)" or "Amount (coins)". */
export function amountInputLabel(label: string, currency: Currency): string {
  return `${label} (${currency.symbol === "" ? currency.namePlural : currency.symbol})`;
}

/**
 * Cents → the plain string an `<input type="number">` holds: no symbol, no grouping, the
 * fraction digits the multiplier implies ("12.50" for 1,250 cents in credits, "1250" in a unit currency).
 */
export function amountInputValue(cents: number, currency: Pick<Currency, "multiplier">): string {
  return (cents / currency.multiplier).toFixed(currencyFractionDigits(currency.multiplier));
}

/** One cent in display units — the `step` and `min` of an amount input ("0.01" for credits). */
export function amountInputStep(currency: Pick<Currency, "multiplier">): string {
  return amountInputValue(1, currency);
}

export function capitalize(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}
